import { IntakeFeedRow } from "@/models/intakeFeedRow";
import { normalizePARow } from "@/composables/usePATable";
import { caseModule, withMedicalNecessity } from "@/models/caseModules";
import { moduleStepRoute } from "@/models/intakeSteps";
export const STATE_LABELS = {
    NOT_STARTED: "Not started",
    READY: "Ready",
    IN_PROGRESS: "In progress",
    WAITING: "Waiting",
    NEEDS_ATTENTION: "Needs attention",
    DONE: "Done",
    NOT_NEEDED: "Not needed",
    CANCELLED: "Cancelled",
};
export const OPEN_STATES = new Set([
    "NOT_STARTED",
    "READY",
    "IN_PROGRESS",
    "WAITING",
    "NEEDS_ATTENTION",
]);
export const STATE_TONES = {
    NEEDS_ATTENTION: "attention",
    READY: "ready",
    IN_PROGRESS: "progress",
    WAITING: "waiting",
    NOT_STARTED: "idle",
    DONE: "done",
    NOT_NEEDED: "muted",
    CANCELLED: "muted",
};
export class WorklistRow {
    id;
    kind;
    pa;
    order;
    document;
    appeal;
    chips;
    isOpen;
    workPriority;
    priority;
    soonestExpiry;
    dueAt;
    lastActivityAt;
    createdOn;
    hasPatient;
    raw;
    casePa;
    /** An intake perspective acts on the intake record, as the Intake tab does, even once the case has a PA. */
    constructor(raw, perspective = "case", medicalNecessity = true) {
        this.raw = raw;
        this.id = raw.id;
        this.casePa = raw.pa ? normalizePARow(raw.pa) : null;
        this.order = raw.order ? new IntakeFeedRow(raw.order, "order") : null;
        this.document = raw.document ? new IntakeFeedRow(raw.document, "document") : null;
        this.appeal = raw.appeal ?? null;
        this.pa = perspective === "intake" && this.intake ? null : this.casePa;
        this.kind = this.pa
            ? "pa"
            : this.order
                ? "order"
                : this.document
                    ? "document"
                    : this.appeal
                        ? "appeal"
                        : "document";
        this.isOpen = !!raw.isOpen;
        this.workPriority = raw.workPriority;
        this.priority = raw.priority;
        this.soonestExpiry = raw.soonestExpiry;
        this.dueAt = raw.dueAt;
        this.lastActivityAt = raw.lastActivityAt;
        // An appeal row is dated by its appeal, the date the Worklist's created sort and filter read.
        this.createdOn = raw.appeal?.createdOn ?? raw.createdOn;
        this.hasPatient = raw.patientId !== null;
        this.chips = withMedicalNecessity(raw.activities, medicalNecessity).map((activity) => this.chip(activity));
    }
    /** The record the row acts on: the PA once there is one, else the intake order, else the document. */
    get primary() {
        return this.pa ?? this.order ?? this.document;
    }
    get intake() {
        return this.order ?? this.document;
    }
    get typeLabel() {
        if (this.appeal)
            return "Appeal";
        return this.pa ? "Prior auth" : "Intake";
    }
    get patientName() {
        if (this.appeal)
            return `${this.appeal.patientFirstName} ${this.appeal.patientLastName}`.trim();
        return (this.casePa ?? this.intake)?.patientName ?? "";
    }
    get sentAt() {
        return this.appeal?.sentAt ?? null;
    }
    get mrn() {
        return this.casePa?.mrn || this.intake?.mrn || "";
    }
    get medication() {
        return this.casePa?.medication || this.intake?.medication || this.appeal?.medication || "";
    }
    get payer() {
        return this.casePa?.insurance || this.intake?.insurance || this.appeal?.payerName || "";
    }
    /** The record whose provider the row shows; name and phone both come from it, so they describe one person. */
    get providerSource() {
        if (this.casePa?.provider)
            return this.casePa;
        if (this.intake?.provider)
            return this.intake;
        return this.casePa ?? this.intake;
    }
    get provider() {
        return this.providerSource?.provider || "";
    }
    get providerPhone() {
        return this.providerSource?.providerPhone || "";
    }
    get providerPhoneExtension() {
        const source = this.providerSource;
        return source instanceof IntakeFeedRow ? source.providerPhoneExtension : "";
    }
    get insurance() {
        return this.payer;
    }
    get planType() {
        return this.casePa?.planType ?? "";
    }
    get priorAuthNumber() {
        return this.casePa?.priorAuthNumber ?? "";
    }
    get prescriptionNumber() {
        return this.casePa?.prescriptionNumber ?? "";
    }
    get dateCreated() {
        return this.casePa?.dateCreated ?? this.intake?.dateReceived ?? this.createdOn;
    }
    get dateReceived() {
        return this.intake?.dateReceived ?? this.casePa?.dateCreated ?? this.createdOn;
    }
    get dateModified() {
        return this.primary?.dateModified ?? null;
    }
    get modifiedBy() {
        return this.primary?.modifiedBy ?? "";
    }
    /** The fax's page types; a case with no fax of its own lists those of the faxes filed onto it. */
    get documentTypes() {
        if (this.document)
            return this.document.documentTypes;
        return [...new Set((this.raw.filedDocuments ?? []).flatMap((doc) => doc.documentTypes))].join(", ");
    }
    /** The fax's sender; a case with no fax of its own lists the senders of the faxes filed onto it. */
    get sentBy() {
        if (this.document)
            return this.document.sentBy;
        const senders = (this.raw.filedDocuments ?? []).map((doc) => doc.submittedByName).filter(Boolean);
        return [...new Set(senders)].join(", ");
    }
    /** Read state is per case; the record the row menu acts on carries it, so marking it updates the row. */
    get isUnread() {
        return this.primary ? this.primary.isUnread : !!this.raw.isUnread;
    }
    get isSnoozed() {
        return !!this.raw.isSnoozed;
    }
    /** Where opening the row goes: the appeal's page, else the primary record's current step. Chips open a step by module. */
    get navigateTo() {
        if (this.appeal)
            return { name: "appeal-detail", params: { id: this.appeal.id } };
        return this.primary?.navigateTo ?? null;
    }
    /** The status of the most urgent open fax filed onto a case with no document of its own, as the server stamps it. */
    get filedFaxStatus() {
        return this.raw.document ? null : this.raw.docStatus;
    }
    /** Open intake of faxes filed onto the case: the row shows another record, so it names them. */
    get filedFaxes() {
        const own = this.raw.document?.key;
        return this.chips.filter((chip) => chip.moduleKey === "fax_intake" && chip.isOpen && chip.documentKey && chip.documentKey !== own);
    }
    chip(activity) {
        const isOpen = OPEN_STATES.has(activity.state);
        return {
            id: activity.id,
            moduleKey: activity.moduleKey,
            moduleLabel: caseModule(activity.moduleKey).label,
            state: activity.state,
            stateLabel: STATE_LABELS[activity.state],
            tone: STATE_TONES[activity.state],
            text: activity.outcome || activity.reason || activity.label || STATE_LABELS[activity.state],
            isOpen,
            documentKey: activity.intakeDocumentKey,
            to: this.routeFor(activity),
        };
    }
    /** A chip opens its own module's page, read only once that step is closed; a fax is editable only when ready. */
    routeFor(activity) {
        const readOnly = activity.moduleKey === "fax_intake" ? activity.state !== "READY" : !OPEN_STATES.has(activity.state);
        const links = { documentKey: activity.intakeDocumentKey, intakeOrderId: activity.intakeOrder ?? this.order?.id };
        return moduleStepRoute(activity.moduleKey, links, readOnly) ?? this.recordRouteFor(activity);
    }
    recordRouteFor(activity) {
        if (activity.orderInfo && this.casePa)
            return this.casePa.navigateTo;
        if (activity.intakeOrder && this.order)
            return this.order.navigateTo;
        if (activity.intakeDocument && this.document)
            return this.document.navigateTo;
        return this.order?.navigateTo ?? null;
    }
}
